import { Loader2 } from "lucide-react";

type ResourceCost = {
  resource_type: string;
  name: string;
  monthly_cost: number;
};

interface CostSummaryProps {
  summary: {
    total_monthly_cost: number | null;
    past_total_monthly_cost: number | null;
    diff: number | null;
    resource_costs: ResourceCost[] | null;
  } | null;
}

export default function CostSummary({ summary }: CostSummaryProps) {
  // Handle loading state when summary is null
  if (summary === null) {
    return (
      <div className="my-6 flex justify-center">
        <div className="w-full max-w-2xl rounded-2xl border border-gray-100 bg-white shadow-lg p-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center space-x-3">
              <Loader2 className="h-8 w-8 text-gray-600 animate-spin" />
              <span className="text-lg font-medium text-gray-700">
                Calculating Infrastructure Costs
              </span>
            </div>
            <p className="text-sm text-gray-500 text-center max-w-md">
              We're analyzing your infrastructure configuration to generate an
              accurate cost estimate. This may take a few moments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Handle empty or invalid summary
  if (
    !summary ||
    !summary.resource_costs ||
    summary.resource_costs.length === 0
  ) {
    return (
      <div className="my-6 flex justify-center">
        <div className="w-full max-w-2xl border border-gray-100 bg-white p-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="text-center text-gray-500">
              <p>Please generate Terraform code to view the cost breakdown.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-6 flex justify-center">
      <div className="w-full max-w-2xl rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-200 p-6">
        <div>
          <div className="font-semibold mb-2 text-gray-700">
            Resource Breakdown
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
            <table className="min-w-full text-sm rounded-xl overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-2 text-left font-semibold text-gray-600">
                    Resource Type
                  </th>
                  <th className="px-6 py-2 text-right font-semibold text-gray-600">
                    Monthly Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.resource_costs?.map(
                  (res: ResourceCost, idx: number) => (
                    <tr
                      key={idx}
                      className={
                        idx % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-green-50 transition"
                      }
                    >
                      <td className="px-6 py-2 text-gray-700 whitespace-nowrap">
                        {res.resource_type}
                      </td>
                      <td className="px-6 py-2 font-mono font-medium text-right whitespace-nowrap">
                        ${res.monthly_cost?.toFixed(2)}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Cost stats at bottom */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-5">
          <div className="flex flex-col items-center border border-gray-200 rounded-lg p-2 bg-white">
            <span className="text-xs text-gray-500">Total Monthly</span>
            <span className="text-base font-mono font-semibold text-green-600">
              ${summary.total_monthly_cost?.toFixed(2) ?? "N/A"}
            </span>
          </div>
          <div className="flex flex-col items-center border border-gray-200 rounded-lg p-2 bg-white">
            <span className="text-xs text-gray-500">Previous Month</span>
            <span className="text-sm font-mono text-gray-700">
              ${summary.past_total_monthly_cost?.toFixed(2) ?? "N/A"}
            </span>
          </div>
          <div
            className={`flex flex-col items-center border border-gray-200 rounded-lg p-2 bg-white ${
              summary.diff != null && summary.diff >= 0
                ? "border-green-200"
                : "border-red-200"
            }`}
          >
            <span className="text-xs text-gray-500">Difference</span>
            <span
              className={`text-sm font-mono font-semibold ${
                summary.diff != null && summary.diff >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {summary.diff != null ? `$${summary.diff.toFixed(2)}` : "N/A"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
